import { get, merge, set, update } from 'lodash'

/**
 * This produces a valid AC that can be used to display an error message
 * @param errorObject - The object that can be merged into the card
 * @param errorObject.id - The notification id to display
 * @param errorObject.title - The notification title to display
 * @param errorObject.code - The CODE for the error
 * @param errorObject.message - A custom message from this error to be displayed
 * @param errorObject.additionalActions - Additional actions to perform
 * @param errorObject.addedEmailMessage - A bit of extra text to add to the email message
 */
const getErrorCard = (errorObject: any = {}) => {
  const baseNotification = {
    id: 'error-1',
    viewed: true,
    card: {
      body: [
        {
          type: 'Container',
          speak: '',
          items: [
            {
              type: 'Image',
              horizontalAlignment: 'center',
              size: 'small',
              url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAYAAADDPmHLAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAABWNJREFUeNrsXdtx2zAQBE4pgKkgjCtQB5ZnXEA+82eqA3ZAuwKW4KQPz8ifrsJxB3E6CJGhE4+iB0jicXfYndFwLEISgbtd8NYgaQwAAAAAAAAAAAAAAAAAAPphS+349fX17bDpxj/vHh4ebpEA5QR/M2x2e29fDUnwWNpYUKEC0Hm+hwRQyP4vw2ZzYNdm3IcEUI5+5j6VWBXG/nbYfD3RpLq4uPj1/Pz8hJNAfcGvhs0PF+QzTV+H1+fhhPAVU4AutB7BN2ObFgqgi/31yP4pcCrwAgXQW/bF+AwUgCH7Xcm3m/lx9eYQgf1lqwApZ/8x08cX6s0h7QrQM/kOtlgpZv8508cXqs0hqzT4vqaPL9SaQ1qngDZg8I1RbA5ZheyvzXTTxxfqzCGNCtAJ/W4oQAD2u5JvF/lnVJlDBPaXrQKkiP1LTR9fqDKHNClAr/S3omKlhP2hTB/vslCLOWQVBD+06eMLFeaQhimgzRB8Y5SYQ1Y4+2sTz/TxhWhzSLoCdDiGQhUgkenjC7HmEIH9ZasACWV/KtPHF2LNIakK0OOYwmAlkP2pTR/vslCiOWSFBT+X6eMLceaQtCmgZRx8YwSaQ1YQ+2uT3/TxhRhzSJICdDjWQhWAmenjCxHm0IeCGOV9J7C9O4gtOWb2CUAC2M/N9PGFCHNIwjmA5NU3PRJgGftdSVUH+rrLSG1PoR77gASYEfzK6FiB2419QQJMBHfTxxeszSFiyv46AvvrSG19VaBGAqQt+zglQKw+6UuA0fRpjD40Y9+QABKZorVvxIz9UU2f4fvXIdosADtziJsCxDZOqkBtOPdRZgIENn04g5U5REyCn8r04aAAb2VhhQT4h1SmzzpQmxCJ2CIBTDTTR0RFwMEc4qAART6rh0vfKTP7XcnXJPzJT4HahEJ2c4gKY0AdqI0aFaCM7Je60ic0sppDORWgR+zzjwVlYn8u02cTqE3wqSmXOUQZgq9lpU+MsrAqQQG0rPQJjSzmkE3Mfif7uS/v+njs4s2RgT8zH1/Sy8pSKwAH6V/P3KdyjCgh+93JVQOlP4uk5hAVxn4xJ4SqEoCZ6cN9CvhTiqYyh1IpACfTp5q5LzV6FQlQ0Eqf0EhiDlHk4MP0WXguENsciq0AHE2fy5n7ck1XrcgEKHilTwwVqCUqAIIvYCwpEvtdyddwPbmauS8noplDVCD7JSZAtDGlCOzHSp84iGIOxVAArPSJh551AkgxfQ5dABr5otBg01doc4gCDqok06fyfI9rWRjsWEPeKFLSSp9+GMRXoQnwZg7dhvgyG4j9Tval3MhZC4KsHAo1BcD0EVoW2gDsdyXfDvHIgsU3pCawv2wVoIXsh+mTF4vNoaUKANOHQUWz5MOrBezn+vSu0rDoaWV2ZvC5P72rNMx+WtncKUD65V3uzHnrzqLH19YIeLrHKRUwM1cO2Rnsr41s02c7MOXbkb41w+ZecN8mm0NzFKDTGHyHcd+2pLLQTmS/K/mkmj6PQ4CvPPu5E1zeTjKHqCD2f4/UVrQK0AT2Szd9XiK15YZJ5tAUBYDpIwd90ARQcnlXHakty776rhwij+BrubzrJlJbtucCPiuHfBRAyz19NmOdfy7hG6PjH1xe5pA9MxhOCrWt9NFsBB3CSXPo3JpAjf/rvx8CfTOWem5gnH++HmV/o7C/LobbyQqAlT6qcNQcosLYXyq6SSeBWOmjDkfNIVpqJABicDCmqwPsx0ofnTi4csjuBR8rfXTjv5VD+1MAbuSsXAXMnjlk37G/Nri8qxT8NYcIZV/ZZeH7BGgwLsWgOVcGAoXgfQLcYTiKAWINAAAAAAAAAMXitwADAKaSZ7i9hFlQAAAAAElFTkSuQmCC'
            },
            {
              type: 'TextBlock',
              text: 'An error has occurred',
              wrap: false,
              weight: 'bolder',
              size: 'medium',
              horizontalAlignment: 'center'
            },
            {
              type: 'TextBlock',
              text: 'Please contact your administrator and inform them of the following error.'
            },
            {
              type: 'TextBlock',
              horizontalAlignment: 'center',
              text: '[UNCOMMON]'
            }
          ]
        }
      ],
      actions: []
    }
  }

  const notification = merge({}, baseNotification)

  if (errorObject.message) {
    const message = errorObject.message
    set(notification, 'card.body[0].items[2].text', message)
  }

  // Add any additional actions

  set(notification, 'card.actions', get(notification, 'card.actions', []).concat(errorObject.additionalActions || []))

  if (errorObject.code) {
    set(notification, 'card.body[0].items[3].text', `[${errorObject && errorObject.code}]`)
  }

  if (errorObject.id || errorObject.title) {
    const message = `${errorObject.title ? `**${errorObject.title}**\n\n` : ''}${
      errorObject.id ? `ID: ${errorObject.id}` : ''
    }`
    update(notification, 'card.body[0].items', items => {
      items.splice(2, 0, {
        type: 'TextBlock',
        text: message
      })
      return items
    })
  }

  return notification
}

export { getErrorCard }
